import { ApiError, apiJson } from '../api';
import { getAccessToken } from '../auth';

/** One of the signed-in account's recent searches (`GET /api/searches/recent`). */
export interface RecentSearch {
  search_id: string;
  description_id: string;
  description: string;
  title?: string | null;
  created_at: string;
  status: string;
}

/** One search from any account, as the admin's search list shows it (`GET /api/admin/searches`). */
export interface AdminSearch {
  search_id: string;
  description_id: string;
  description: string;
  user_id: string | null;
  username: string | null;
  window_hours: number | null;
  candidate_count: number | null;
  cached_count: number | null;
  model: string | null;
  created_at: string;
}

export async function listRecentSearches(): Promise<RecentSearch[]> {
  const data = await apiJson<RecentSearch[]>('/api/searches/recent');
  return Array.isArray(data) ? data : [];
}

/** Every account's searches, newest first (admin). */
export async function listAdminSearches(limit: number, offset: number): Promise<AdminSearch[]> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const data = await apiJson<AdminSearch[]>(`/api/admin/searches?${params}`);
  return Array.isArray(data) ? data : [];
}

export interface SearchDescription {
  id: string;
  description: string;
  short_title?: string | null;
}

export async function resolveSearchDescription(description: string): Promise<SearchDescription> {
  return apiJson<SearchDescription>('/api/search-descriptions', {
    method: 'POST',
    json: { description },
    public: !getAccessToken(),
  });
}

// A description read already in flight is shared, not repeated: the reader's
// description read and its title follow-up can ask for the same row at once,
// and a second request would only duplicate the first (PRO-390).
const descriptionReads = new Map<string, Promise<SearchDescription>>();

export function getSearchDescription(descriptionId: string): Promise<SearchDescription> {
  const inFlight = descriptionReads.get(descriptionId);
  if (inFlight) return inFlight;
  const read = apiJson<SearchDescription>(
    `/api/search-descriptions/${encodeURIComponent(descriptionId)}`,
    { public: !getAccessToken() },
  ).finally(() => descriptionReads.delete(descriptionId));
  descriptionReads.set(descriptionId, read);
  return read;
}

/** The description and window a search id ran. */
export interface SearchRunDescription {
  description_id: string;
  window_hours: number | null;
}

/**
 * What any search id ran: a search run, a recorded reader search, or a retired
 * fast-search run. A public read, so a signed-out `/?run=` link resolves too.
 */
export function getSearchRunDescription(searchId: string): Promise<SearchRunDescription> {
  return apiJson<SearchRunDescription>(
    `/api/search-runs/${encodeURIComponent(searchId)}/description`,
    { public: !getAccessToken() },
  );
}

/** The search a `/search/<id>` URL names: its description (id and text), and the window an old run ran. */
export interface NamedSearch {
  descriptionId: string;
  description: string;
  windowHours: number | null;
}

/**
 * The search `searchId` names: read as a description id (what search URLs
 * carry), or, failing that, as an old search run's id, with the window it ran.
 * Rejects when it is neither.
 */
export async function resolveSearchId(searchId: string): Promise<NamedSearch> {
  try {
    const description = await getSearchDescription(searchId);
    return { descriptionId: description.id, description: description.description, windowHours: null };
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 404) throw err;
  }
  const run = await getSearchRunDescription(searchId);
  const description = await getSearchDescription(run.description_id);
  return { descriptionId: description.id, description: description.description, windowHours: run.window_hours };
}
